import { Link } from "react-router-dom";
import { ArrowRight, Building2, ShieldCheck, FileCheck, Wrench, Phone, Mail } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const AREE = [
  {
    icon: FileCheck,
    titolo: "Verifica delle autorizzazioni amministrative",
    testo: "Analizziamo la completezza e la validità delle autorizzazioni amministrative necessarie per l'esercizio dell'attività, individuando eventuali criticità da sanare.",
  },
  {
    icon: Building2,
    titolo: "Conformità edilizia e urbanistica",
    testo: "Verifichiamo la conformità dei locali commerciali rispetto alle normative in materia di edilizia e urbanistica, requisito fondamentale per l'avvio o l'aggiornamento dell'attività.",
  },
  {
    icon: ShieldCheck,
    titolo: "Sicurezza strutturale",
    testo: "Analizziamo la sicurezza strutturale degli edifici e degli impianti presenti nei locali, a tutela dell'impresa, dei lavoratori e della clientela.",
  },
  {
    icon: Wrench,
    titolo: "Efficienza delle attrezzature",
    testo: "Controlliamo l'efficienza e la conformità delle attrezzature in uso, verificandone la corretta manutenzione e il rispetto degli standard di legge.",
  },
];

export default function CheckUpTecnicoAmministrativoPage() {
  return (
    <main id="main-content" data-testid="check-up-tecnico-amministrativo-page">
      {/* Hero */}
      <section className="bg-[var(--confcom-blue)] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeroBreadcrumb />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Check-up tecnico-amministrativo
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            Una verifica gratuita per la messa in regola dei locali commerciali.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            Il <strong className="text-[var(--confcom-text-primary)]">C.A.T. – Centro Assistenza Tecnica</strong> di Confcommercio Roma offre alle imprese associate un check-up tecnico-amministrativo gratuito, finalizzato a valutare l'<strong className="text-[var(--confcom-text-primary)]">idoneità dei locali commerciali</strong> rispetto alle normative vigenti in materia di edilizia, urbanistica e conformità amministrativa.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            L'obiettivo è supportare le imprese nell'<strong className="text-[var(--confcom-text-primary)]">avvio o nell'aggiornamento dell'attività</strong>, garantendo il rispetto delle leggi italiane ed europee e fornendo una guida pratico-operativa per la messa in regola definitiva.
          </p>
        </div>
      </section>

      {/* Aree di verifica */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-3 block">Cosa comprende il servizio</span>
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-10">
            Quattro aree di verifica
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {AREE.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.titolo}
                  className="bg-white p-7 rounded-xl border border-[var(--confcom-border)]"
                  data-testid={`area-${item.titolo.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-11 h-11 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center shrink-0">
                      <Icon size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] pt-1.5">
                      {item.titolo}
                    </h3>
                  </div>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">
                    {item.testo}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.95)" }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-4">Per maggiori informazioni</h2>
          <p className="text-white/80 mb-8">
            Contatta il C.A.T. – Centro Assistenza Tecnica per richiedere il check-up tecnico-amministrativo gratuito dei tuoi locali.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-2">
            <a
              href="mailto:servizi-cat@confcommercioroma.it"
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity"
              data-testid="cta-email-cat"
            >
              <Mail size={16} aria-hidden="true" /> servizi-cat@confcommercioroma.it
            </a>
            <a
              href="tel:+390668437369"
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity"
              data-testid="cta-phone-cat"
            >
              <Phone size={16} aria-hidden="true" /> 06.68437369
            </a>
          </div>
          <Link
            to="/contatti"
            className="inline-flex items-center gap-1 text-white/70 hover:text-white transition-colors text-sm mt-6"
          >
            Vai alla pagina Contatti <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
}
